/**
 * External Imports
 */
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { add, format } from 'date-fns';
import { pdf } from '@react-pdf/renderer';

/**
 * Internal Imports
 */
import SearchHeader from '../../../components/searchHeader';
import EmployeeFrequencyReportTable from '../../../components/customTables/reports/employee/frequencyReportTable';
import EmployeeFrequencyPdfTableFile from '../../../components/shared/buttons/DownloadPdfBtn/PdfTable/employee/EmployeeFreqencyPdfTableFile';

import { RootState } from '../../../redux/store';
import {
  clearEmployeeFreqReport,
  clearFrequencyReportFilter,
  getCSVToExportSuccess
} from '../../../redux/actions';

import { date } from '../../../utils/helpers';
import {
  useGetEmployeeFrequencyReportQuery,
  useLazyGetFrequencyReportDownloadQuery
} from '../../../redux/apis/Reports.api';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  EmpFreq,
  Filter,
  RawEmpFreq
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { ReportContainer } from '../styles/ReportsViews.styles';

interface FreqReportProps {
  authorizedUser: AuthUserProps;
  currentEmployeeFreqReportPage: number;
  frequencyReportFilter: Filter;
  reportSearchQuery: string;
  searchTerm: string;
  sortKey: string;
  sortDir: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const FreqReport: FC<FreqReportProps> = ({
  authorizedUser,
  currentEmployeeFreqReportPage,
  frequencyReportFilter,
  reportSearchQuery,
  searchTerm,
  sortKey,
  sortDir,
  setSearchTerm
}) => {
  const dispatch = useDispatch();
  const [getEmployeeFreqReportDownload] =
    useLazyGetFrequencyReportDownloadQuery();

  const {
    data: employeeFreqReportData,
    // isError: isGetEmployeeFreqReportError,
    isFetching: isGetEmployeeFreqReportFetching,
    isLoading: isGetEmployeeFreqReportLoading
  } = useGetEmployeeFrequencyReportQuery(
    {
      facilityId: authorizedUser?.facilityId,
      query: reportSearchQuery || '',
      sortBy: sortKey,
      sortOrder: sortDir,
      startday: date.formatFilterStartDayToUTCTimestamp(
        (frequencyReportFilter.startDate ?? date.firstDayOfMonth()).replace(
          /-/g,
          ''
        )
      ),
      endday: date.formatFilterEndDayToUTCTimestamp(
        format(
          add(
            date.localDayOfYearDate(
              frequencyReportFilter.endDate ?? date.todayDateWithDashes()
            ),
            { days: 1 }
          ),
          'yyyy-MM-dd'
        ).replace(/-/g, '')
      ),
      templateNames: frequencyReportFilter.templateNames ?? [],
      page: currentEmployeeFreqReportPage
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !authorizedUser?.facilityId
    }
  );

  useEffect(() => {
    return () => {
      dispatch(clearEmployeeFreqReport());
      dispatch(clearFrequencyReportFilter());
    };
  }, []);

  const listToDisplay: EmpFreq[] = useMemo(() => {
    if (employeeFreqReportData?.freqReportList?.length) {
      return employeeFreqReportData?.freqReportList?.map(
        (freq: RawEmpFreq) => ({
          facilityId: freq?.facilityId || '',
          department: freq?.employeeDept || '',
          employee:
            `${freq?.employeeFirstName} ${freq?.employeeLastName}` || '',
          employeeId: freq?.employeeId || '',
          employeeFirstName: freq?.employeeFirstName || '',
          employeeLastName: freq?.employeeLastName || '',
          count: freq?.count || 0
        })
      );
    }
    return [];
  }, [employeeFreqReportData]);

  const handleExportCsv = useCallback(() => {
    getEmployeeFreqReportDownload(
      {
        facilityId: authorizedUser?.facilityId,
        query: reportSearchQuery || '',
        sortBy: sortKey,
        sortOrder: sortDir,
        startday: date.formatFilterStartDayToUTCTimestamp(
          (frequencyReportFilter.startDate ?? date.firstDayOfMonth()).replace(
            /-/g,
            ''
          )
        ),
        endday: date.formatFilterEndDayToUTCTimestamp(
          format(
            add(
              date.localDayOfYearDate(
                frequencyReportFilter.endDate ?? date.todayDateWithDashes()
              ),
              { days: 1 }
            ),
            'yyyy-MM-dd'
          ).replace(/-/g, '')
        ),
        templateNames: frequencyReportFilter.templateNames
      },
      true
    )
      .unwrap()
      .then(data => dispatch(getCSVToExportSuccess(data)))
      .catch(() => {});
  }, [
    authorizedUser?.facilityId,
    reportSearchQuery,
    sortKey,
    sortDir,
    frequencyReportFilter
  ]);

  const handleClickDownloadPDF = async () => {
    const asPdf = pdf(<EmployeeFrequencyPdfTableFile data={listToDisplay} />);

    const blob = await asPdf.toBlob();
    function downloadBlob(name = 'file.txt') {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = name;

      document.body.appendChild(link);
      link.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
      );
      document.body.removeChild(link);
    }

    return downloadBlob(`emp-frequency-report-${new Date().toISOString()}.pdf`);
  };

  return (
    <ReportContainer id="Employee-Rounds-Report-Container">
      <SearchHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleExportCsv={handleExportCsv}
        handleClickDownloadPDF={handleClickDownloadPDF}
      />
      <EmployeeFrequencyReportTable
        frequencyReportList={listToDisplay}
        loading={
          isGetEmployeeFreqReportFetching || isGetEmployeeFreqReportLoading
        }
        totalTableCount={employeeFreqReportData?.totalRecordCount}
      />
    </ReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser.authorizedUser,
  currentEmployeeFreqReportPage:
    state.ReportReducer.currentEmployeeFreqReportPage,
  reportSearchQuery: state.ReportReducer.reportSearchQuery,
  sortKey: state.ReportReducer.sortFreqKey,
  sortDir: state.ReportReducer.sortFreqDir,
  frequencyReportFilter: state.ReportReducer.frequencyReportFilter
});

export default connect(mapReduxStateToProps)(FreqReport);
